import { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { ListChecks } from 'lucide-react';
import { Button, Loading, EmptyState, ErrorState } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../../../api/errorMapping';
import { useTenantUserFields } from '../../../../hooks/useTenantUserFields';
import { Gate } from '../../../../components/permissions/Gate';
import { Badge } from '../../../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import { DataTableToolbar } from '../../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../../components/shared/SortableHeader';
import { globalFilterFn } from '../../../../lib/dataTable';
import { UserFieldForm } from './UserFieldForm';
import { UserFieldRowActions } from './UserFieldRowActions';
import type { UserFieldDefLocal } from '../../../../api/types/TenantLocal';

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). Toda la data se consume contra ESTE tenant. */
    tenantId: string;
}

/**
 * Catálogo de campos personalizados de usuario de un tenant (drill-down PLATFORM). Muestra TODOS los
 * campos (incluidos los deshabilitados): `useTenantUserFields(tenantId)` sin `enabledOnly`. Misma
 * composición que el resto de tablas del drill-down (useReactTable + Toolbar/Pagination +
 * SortableHeader + kebab por fila). Orden por defecto: alfabético por etiqueta (default del
 * proyecto). Crear/editar/eliminar/deshabilitar gateados por `platform.tenant.userfields.manage`;
 * el backend re-enforza.
 */
export function UserFieldsTable({ tenantId }: Props) {
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    // Orden por defecto: alfabético ascendente por la etiqueta visible (default del proyecto).
    const [sorting, setSorting] = useState<SortingState>([{ id: 'label', desc: false }]);

    const { data, isLoading, isError, error, refetch } = useTenantUserFields(tenantId);

    const columns = useMemo<ColumnDef<UserFieldDefLocal>[]>(
        () => [
            {
                accessorKey: 'label',
                header: ({ column }) => <SortableHeader column={column} title="Etiqueta" />,
            },
            {
                id: 'type',
                accessorFn: (row) => (row.type === 'select' ? 'Lista' : 'Texto'),
                header: ({ column }) => <SortableHeader column={column} title="Tipo" />,
                cell: (info) => <Badge variant="secondary">{info.getValue() as string}</Badge>,
            },
            {
                id: 'required',
                accessorFn: (row) => (row.required ? 'Sí' : 'No'),
                header: ({ column }) => <SortableHeader column={column} title="Requerido" />,
                enableGlobalFilter: false,
                cell: (info) =>
                    info.row.original.required ? (
                        <Badge variant="default">Sí</Badge>
                    ) : (
                        <span className="text-muted-foreground">No</span>
                    ),
            },
            {
                id: 'injectToToken',
                accessorFn: (row) => (row.injectToToken ? 'Sí' : 'No'),
                header: ({ column }) => (
                    <SortableHeader column={column} title="Se incluye en la sesión" />
                ),
                enableGlobalFilter: false,
                cell: (info) =>
                    info.row.original.injectToToken ? (
                        <Badge variant="default">Sí</Badge>
                    ) : (
                        <span className="text-muted-foreground">No</span>
                    ),
            },
            {
                id: 'enabled',
                accessorFn: (row) => (row.enabled ? 'Habilitado' : 'Deshabilitado'),
                header: ({ column }) => <SortableHeader column={column} title="Estado" />,
                enableGlobalFilter: false,
                cell: (info) =>
                    info.row.original.enabled ? (
                        <Badge variant="default">Habilitado</Badge>
                    ) : (
                        <Badge variant="secondary">Deshabilitado</Badge>
                    ),
            },
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: (info) => (
                    <UserFieldRowActions field={info.row.original} tenantId={tenantId} />
                ),
            },
        ],
        [tenantId],
    );

    const rows = useMemo(() => data ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isLoading) return <Loading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar los campos"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="platform.tenant.userfields.manage" scope="PLATFORM">
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear campo
                    </Button>
                </Gate>
            </div>

            {/* Modal plano (mismo patrón que el resto del drill-down): crear sin navegar de página. */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Crear campo personalizado</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <UserFieldForm
                            tenantId={tenantId}
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            {rows.length === 0 ? (
                <EmptyState
                    icon={<ListChecks className="size-8 opacity-60" />}
                    title="Esta organización no tiene campos personalizados"
                    description="Crea un campo para extender el perfil de los usuarios de esta organización."
                />
            ) : (
                <>
                    <DataTableToolbar
                        value={globalFilter}
                        onChange={setGlobalFilter}
                        placeholder="Buscar por etiqueta…"
                    />

                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map((h) => (
                                        <TableHead key={h.id} className="text-center">
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((r) => (
                                <TableRow key={r.id}>
                                    {r.getVisibleCells().map((c) => (
                                        <TableCell key={c.id} className="text-center align-middle">
                                            {flexRender(c.column.columnDef.cell, c.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <DataTablePagination table={table} />
                </>
            )}
        </div>
    );
}
